
import { AppEvents, TemplateGenerationStartedEvent, TemplateGenerationCompleteEvent, TemplateGenerationFailedEvent, TemplateContentUpdateEvent } from '@/types/events';

/**
 * Event handler utilities for cross-component communication
 */

/**
 * Emit an event to notify components to start a new conversation
 * @param chatId Optional chat ID for the new conversation
 */
export function emitNewConversation(chatId?: string): void {
  console.log("[EVENT] Emitting new conversation event with chatId:", chatId);
  const event = new CustomEvent('start-new-conversation', { 
    detail: { chatId },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Emit an event to notify components to load a specific conversation
 * @param chatId The ID of the chat to load
 */
export function emitLoadConversation(chatId: string): void {
  console.log("[EVENT] Emitting load conversation event with chatId:", chatId);
  const event = new CustomEvent('load-conversation', { 
    detail: chatId,
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Open an email template
 * @param templateId The ID of the template to open
 */
export function openEmailTemplate(templateId: string): void {
  console.log("[EVENT] Opening email template:", templateId);
  const event = new CustomEvent('open-email-template', { 
    detail: { templateId },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Open the send offer modal
 * @param url The URL to send
 * @param title The title of the offer
 */
export function openSendOfferModal(url: string, title: string): void {
  console.log("[EVENT] Opening send offer modal for:", title);
  const event = new CustomEvent('open-send-offer-modal', { 
    detail: { url, title },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Open a specific chat
 * @param chatId The ID of the chat to open
 */
export function openChat(chatId: string): void {
  console.log("[EVENT] Opening chat with ID:", chatId);
  const event = new CustomEvent('open-chat', { 
    detail: { chatId },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Start template generation process
 * @param concept The concept to generate a template from
 */
export function startTemplateGeneration(concept: string): void {
  console.log("[EVENT] Starting template generation with concept:", concept.substring(0, 50));
  const event = new CustomEvent<TemplateGenerationStartedEvent>('template-generation-started', { 
    detail: { concept },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Notify that template generation is complete
 * @param content The generated template content
 * @param isStreamingComplete Whether this is from a streaming completion
 */
export function completeTemplateGeneration(content: string, isStreamingComplete = false): void {
  console.log("[EVENT] Template generation complete, content length:", content.length);
  const event = new CustomEvent<TemplateGenerationCompleteEvent>('template-generation-complete', { 
    detail: { 
      content,
      isStreamingComplete 
    },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Notify that template generation failed
 * @param error The error message
 */
export function failTemplateGeneration(error: string): void {
  console.error("[EVENT] Template generation failed:", error);
  const event = new CustomEvent<TemplateGenerationFailedEvent>('template-generation-failed', { 
    detail: { error },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Update template content during generation
 * @param content The current content
 * @param isPartial Whether this is a partial update
 */
export function updateTemplateContent(content: string, isPartial = false): void {
  console.log("[EVENT] Updating template content, length:", content.length);
  const event = new CustomEvent<TemplateContentUpdateEvent>('template-content-update', { 
    detail: { 
      content,
      isPartial 
    },
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Show the template preview
 */
export function showTemplatePreview(): void {
  console.log("[EVENT] Showing template preview");
  const event = new CustomEvent('show-template-preview', { 
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Show the code view
 */
export function showCodeView(): void {
  console.log("[EVENT] Showing code view");
  const event = new CustomEvent('show-code-view', { 
    bubbles: true 
  });
  window.dispatchEvent(event);
}

/**
 * Notify that a document has been signed
 * @param documentId The ID of the signed document
 * @param chatId The ID of the chat where the document was signed
 */
export function documentSigned(documentId: string, chatId: string): void {
  console.log("[EVENT] Document signed:", documentId, "in chat:", chatId);
  const event = new CustomEvent('document-signed', {
    detail: { documentId, chatId },
    bubbles: true
  });
  window.dispatchEvent(event);
  
  // Also trigger a refresh of the chat list to update the signed document indicators
  emitRefreshChatList();
}

/**
 * Emit an event to refresh the chat list
 */
export function emitRefreshChatList(): void {
  console.log("[EVENT] Refreshing chat list");
  const event = new CustomEvent('refresh-chat-list', {
    bubbles: true
  });
  window.dispatchEvent(event);
}
